import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { VOID_REASONS } from '@/results/results.api';
import { clockText, isoFromLocal, localInput } from '@/results/format';

/** What the operator confirmed (only the fields the dialog asked for are meaningful). */
export interface Confirmed {
  reason: string;
  voidReason: string;
  reopen: boolean;
  force: boolean;
  /** GS-902: an earlier outcome time (ISO), or '' for the server's default (the bet stop). */
  outcomeKnownAt: string;
}

const VOID_LABELS: Record<string, string> = {
  NO_RESULT: 'No result',
  ABANDONED: 'Abandoned',
  TIE: 'Tie (no tie runner)',
  CANCELLED: 'Cancelled',
  OTHER: 'Other',
};

/**
 * GS-901 — every result goes through one confirmation: what will happen, in words, and what engine v2 needs with it (a
 * reason for the audit, why there is no result, whether to reopen) — and, after D1 refused it, whether to force it.
 */
@Component
export default class ConfirmDialog extends Vue {
  @Prop({ default: false }) readonly open!: boolean;
  @Prop({ default: '' }) readonly title!: string;
  @Prop({ default: 'Confirm' }) readonly eyebrow!: string;
  @Prop({ default: (): string[] => [] }) readonly lines!: string[];
  @Prop({ default: 'Confirm' }) readonly confirmLabel!: string;
  /** primary (a result) or danger (an undo, void bets). */
  @Prop({ default: 'primary' }) readonly tone!: string;
  @Prop({ default: false }) readonly needsReason!: boolean;
  @Prop({ default: false }) readonly voidReasons!: boolean;
  @Prop({ default: false }) readonly reopenOption!: boolean;
  @Prop({ default: false }) readonly forceOption!: boolean;
  /** GS-902 (FN-D8, §7.6): offer the outcome time — a v2 SETTLE / RESETTLE. */
  @Prop({ default: false }) readonly outcomeOption!: boolean;
  /** The time the server takes when none is given (a fancy line's / premium market's bet stop), if known. */
  @Prop({ default: null }) readonly outcomeDefault!: string | null;
  @Prop({ default: '' }) readonly error!: string;
  @Prop({ default: false }) readonly busy!: boolean;
  /** What the reason box starts with (a result: the scorecard; a rejection or a write-off: nothing). */
  @Prop({ default: 'Official scorecard' }) readonly defaultReason!: string;

  private reason = '';
  private voidReason = 'NO_RESULT';
  private reopen = false;
  private force = false;
  private earlier = false;
  private outcomeLocal = '';

  get voidReasonOptions(): Array<{ value: string; label: string }> {
    return VOID_REASONS.map((value) => ({ value, label: VOID_LABELS[value] || value }));
  }

  get outcomeNote(): string {
    return this.outcomeDefault ? `Bets placed after the bet stop (${clockText(this.outcomeDefault)}) are void.`
      : 'Every bet stands, unless you set the time the outcome was known.';
  }

  /** The latest time that may be given: the bet stop when known (only EARLIER is a correction of it), else now. */
  get maxLocal(): string {
    return localInput(this.outcomeDefault || new Date().toISOString());
  }

  get outcomeProblem(): string {
    if (!this.earlier) {
      return '';
    }
    const iso = isoFromLocal(this.outcomeLocal);
    if (!iso) {
      return 'Set the date and time.';
    }
    if (Date.parse(iso) > Date.now()) {
      return 'The outcome cannot be known in the future.';
    }
    if (this.outcomeDefault && Date.parse(iso) > Date.parse(this.outcomeDefault)) {
      return 'Only an earlier time than the bet stop changes anything.';
    }
    return '';
  }

  get canConfirm(): boolean {
    if (this.busy) {
      return false;
    }
    if (this.outcomeProblem) {
      return false;
    }
    if (this.needsReason && this.reason.trim().length === 0) {
      return false;
    }
    return !this.forceOption || this.force;
  }

  /** A fresh dialog each time it opens (the force tick never carries over to another result). */
  @Watch('open')
  onOpen(open: boolean): void {
    if (open) {
      this.reason = this.defaultReason;
      this.voidReason = 'NO_RESULT';
      this.reopen = false;
      this.force = false;
      this.earlier = false;
      this.outcomeLocal = localInput(this.outcomeDefault || new Date().toISOString());
    }
  }

  cancel(): void {
    if (!this.busy) {
      this.$emit('cancel');
    }
  }

  confirm(): void {
    if (this.canConfirm) {
      const confirmed: Confirmed = { reason: this.reason.trim(), voidReason: this.voidReason, reopen: this.reopen, force: this.force,
        outcomeKnownAt: this.earlier ? isoFromLocal(this.outcomeLocal) : '' };
      this.$emit('confirm', confirmed);
    }
  }
}
